import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useTranslation } from '@crehana/i18n';
import { Plus4 } from '@crehana/icons';
import { Loader, PrimaryButton } from '@crehana/ui';
import { primaryLight } from '@crehana/ui/dist/styles/colors';

import LegalCheckbox from 'Jsx/CheckoutV4/components/LegalCheckbox';
import QuotasSelector from 'Jsx/CheckoutV4/components/QuotasSelector';
import { useCheckoutContext } from 'Jsx/CheckoutV4/Context';
import I18N_STRINGS from 'Jsx/CheckoutV4/i18n/language';
import PaymentsLoader from 'Jsx/CheckoutV4/shared/PaymentsLoader';
import { CustomerCard, DegreeQuota } from 'Jsx/CheckoutV4/types';
import { loadDlocalScript } from 'Jsx/CheckoutV4/utils/creditCard/loadPaymentMethodScripts';
import { PaymentSourceEnum } from 'Jsx/Types/Graphql/globals.payments';
import getCountryPrefix from 'Jsx/Utils/getCountryPrefix';

import SecurePayment from 'Jsx/CheckoutV4/shared/SecurePayment';

import { PaymentMutationVariables } from '../../../../Graphql/Mutation/types/PaymentMutation';
import useCustomerExtraData from '../../../../hooks/useCustomerExtraData';
import SavedCardsList from '../../../PaymentWithSavedCard/SavedCardsList/SavedCardsList';
import { createDLocalToken, generateDeviceId } from '../utils/checkoutHandling';

import { parseDLocalCardBrand } from '../utils';

import { DLocalData } from './DLocal';

type Brand = {
  localKey: string | null;
  globalKey: string | null;
  loading: boolean;
  paymentError: string;
  paymentGateway: number | null;
  cardBrand?: string;
};

const PaymentWithSavedCard: React.FC<{
  savedCards: CustomerCard[];
  symbol: string | null;
  quotas?: DegreeQuota[];
  selectedQuotaNumber: number;
  setSelectedQuotaNumber: Dispatch<SetStateAction<number>>;
  togglePaymentWithSavedCard(): void;
  isDLocal?: boolean;
  errorMessage: string;
  onCheckout: (extraVariables: Partial<PaymentMutationVariables>) => void;
  processing: boolean;
}> = ({
  savedCards,
  symbol,
  quotas,
  selectedQuotaNumber,
  setSelectedQuotaNumber,
  togglePaymentWithSavedCard,
  isDLocal = false,
  errorMessage,
  onCheckout,
  processing,
}) => {
  const { t } = useTranslation();
  const [savedCard, setSavedCard] = React.useState<Brand>({
    localKey: savedCards[0]?.cardMx || '',
    globalKey: savedCards[0]?.cardUsa || '',
    loading: false,
    paymentError: '',
    paymentGateway: null,
    cardBrand: parseDLocalCardBrand(savedCards[0]?.cardBrand || ''),
  });
  const [dLocalTokenData, setDLocalTokenData] = useState<DLocalData>();
  const [isDlocalLoaded, setIsDlocalLoaded] = useState(false);
  const [checkedTerms, setCheckedTerms] = React.useState(true);
  const [cvvComplete, setCvvComplete] = useState(!isDLocal);
  const { preloadedCourseData } = useCheckoutContext();
  const { customerExtraData } = useCustomerExtraData();
  const parsedSavedCards = useMemo(() => {
    if (!isDLocal) return savedCards;
    return savedCards.map((card: CustomerCard) => {
      if (!card) return card;
      return {
        ...card,
        cardBrand: parseDLocalCardBrand(card?.cardBrand || ''),
      };
    });
  }, [savedCards, isDLocal]);

  const countryPrefix = getCountryPrefix();

  const createSmartField = useCallback(() => {
    const dlocal = window.dlocal(window.DLOCAL_API_KEY);
    const fields = dlocal.fields({
      locale: countryPrefix === 'br' ? 'pt' : 'es',
      country: countryPrefix?.toUpperCase(),
    });

    const style = {
      base: {
        fontSize: '16px',
        lineHeight: '24px',
      },
      focus: {
        borderColor: primaryLight,
        border: '1px solid red',
      },
    };
    const cvvField = fields.create?.('cvv-only', { style });

    cvvField.mount(document.getElementById('cvv-field'));

    cvvField.addEventListener('ready', () => {
      setIsDlocalLoaded(true);
    });

    cvvField.addEventListener('change', (event: { complete: boolean }) => {
      setCvvComplete(event.complete);
    });

    setDLocalTokenData({
      createToken: dlocal.createToken,
      cardField: cvvField,
    });
  }, [countryPrefix]);

  useEffect(() => {
    if (isDLocal) {
      if (!window.dlocal) {
        loadDlocalScript(() => {
          createSmartField();
        });
      } else {
        createSmartField();
      }
    }
  }, [isDLocal, createSmartField]);

  const getDlocalToken = async () => {
    const tokenResponse = await createDLocalToken(dLocalTokenData, {
      isValid: true,
      name: customerExtraData?.fullName || '',
    });

    return tokenResponse.token;
  };

  const onClickPay = async () => {
    setSavedCard({ ...savedCard, loading: true });

    const cardData = isDLocal
      ? { cardId: savedCard.globalKey || '', cvvToken: await getDlocalToken() }
      : {
          stripeCardId: savedCard.globalKey,
          stripeMexicoCardId: savedCard.localKey,
        };

    const deviceId = isDLocal
      ? { deviceId: generateDeviceId().toString() }
      : {};

    onCheckout({
      paymentSource: preloadedCourseData
        ? PaymentSourceEnum.COURSE_DETAIL_POP_UP_VIEW
        : PaymentSourceEnum.CHECKOUT_VIEW,
      ...cardData,
      ...customerExtraData,
      ...deviceId,
    });

    // window.Sentry.withScope(scope => {
    //   scope.setTag('paymentMethod', 'savedCard');
    //   window.Sentry.captureException(err);
    // });
  };

  const getSelectedCard = (selectedCard: CustomerCard) => {
    if (selectedCard) {
      setSavedCard({
        ...savedCard,
        localKey: selectedCard?.cardMx || '',
        globalKey: selectedCard?.cardUsa || '',
        paymentGateway: selectedCard?.paymentGateway || 1,
        cardBrand: selectedCard?.cardBrand || '',
      });
    }
  };

  const isPaymentButtonActive =
    checkedTerms && !!savedCard.globalKey && cvvComplete;

  return (
    <>
      {isDLocal && !isDlocalLoaded && (
        <div className="flex justify-center w-full">
          <Loader size={36} />
        </div>
      )}
      <div className={isDLocal && !isDlocalLoaded ? 'hidden' : 'block'}>
        <h2 className="font-h2 mb-24 md:hidden">{t('GO_SAVED_CARD')}</h2>
        <h3 className="font-h3 mb-24 hidden md:block">{t('GO_SAVED_CARD')}</h3>
        <div style={{ maxWidth: 399 }}>
          <div>
            <SavedCardsList
              getSelectedCard={getSelectedCard}
              savedCards={parsedSavedCards}
            />
            {isDLocal && (
              <div className="my-16">
                <div className="w-full">
                  <label htmlFor="cvv-field" className="font-subtitle3">
                    CVC/CVV
                  </label>
                  <p className="font-body4 my-8 text-gray-dark">
                    {I18N_STRINGS.CVV_VALIDATION}
                  </p>
                  <div
                    id="cvv-field"
                    className="p-16 mt-8 mb-8 bg-white border border-solid border-gray-dark rounded-5 font-subtitle3"
                  />
                </div>
              </div>
            )}
            <button
              className="mt-16 mb-24 text-secondary-main font-link flex items-center"
              onClick={togglePaymentWithSavedCard}
              disabled={savedCard.loading}
              type="button"
            >
              <Plus4
                size={24}
                ignoreColor
                className="fill-current text-secondary-main mr-8"
              />
              <span className="normal-case">{t('ADD_SAVED_CARD')}</span>
            </button>
          </div>

          <QuotasSelector
            symbol={symbol}
            quotas={quotas}
            selectedQuotaNumber={selectedQuotaNumber}
            setSelectedQuotaNumber={setSelectedQuotaNumber}
          />

          <div
            style={{ maxWidth: 320 }}
            className="flex-grow mx-auto mt-24 sm:mx-0"
          >
            <LegalCheckbox
              className="mb-24"
              checkedTerms={checkedTerms}
              setCheckedTerms={setCheckedTerms}
            />

            {errorMessage && (
              <p className="font-caption text-red-main mb-24">{errorMessage}</p>
            )}

            <div className="w-full sm:max-w-360">
              <PrimaryButton
                disabled={processing || !isPaymentButtonActive}
                data-testid="buy-product-submit"
                onClick={onClickPay}
                full
                size="l"
                className="mb-24"
                label={t('BUY_NOW')}
                isAccent
              />
            </div>

            <SecurePayment />
          </div>
          {processing && (
            <PaymentsLoader
              type="purchaseOnline"
              cardType={savedCard.cardBrand}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentWithSavedCard;
